import { SidebarTrigger } from "@/components/ui/sidebar";
import { StatCard } from "@/components/dashboard/StatCard";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, FileText } from "lucide-react";
import { useDashboardStats, useRecentActivity } from "@/hooks/useDashboard";

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: activities, isLoading: activitiesLoading } = useRecentActivity();

  const statsCards = [
    {
      title: "Documents Processed This Week",
      value: stats?.documentsProcessedThisWeek ?? 0,
      description: "Routed to Dropbox",
      icon: FileText,
      variant: "info" as const,
    },
    {
      title: "Validation Issues",
      value: stats?.validationIssues ?? 0,
      description: "Warnings and blocks",
      icon: AlertTriangle,
      variant: "warning" as const,
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b bg-card px-6">
        <SidebarTrigger />
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-foreground">Dashboard</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 space-y-6 p-6">
        {/* Stats Grid */}
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {statsLoading ? (
            <>
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </>
          ) : (
            statsCards.map((stat) => (
              <StatCard key={stat.title} {...stat} />
            ))
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid gap-4">
          <div className="rounded-lg border bg-card p-6">
            <h3 className="mb-4 text-lg font-semibold">Recent Activity</h3>
            {activitiesLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16" />
                ))}
              </div>
            ) : activities && activities.length > 0 ? (
              <div className="space-y-3">
                {activities.map((activity, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/50"
                  >
                    <div
                      className={`mt-0.5 h-2 w-2 rounded-full ${
                        activity.variant === "success"
                          ? "bg-success"
                          : activity.variant === "info"
                          ? "bg-info"
                          : "bg-warning"
                      }`}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.detail}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {activity.time}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No recent activity
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
